import React, { useState, useEffect } from 'react';
import { getFinancialYears, createSession } from '../services/api';
import { logoutUser } from '../services/api';

const CompanyYearSelection = ({ userData, onSelectionComplete, onLogout }) => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [yearsLoading, setYearsLoading] = useState(false);
  const [rememberSelection, setRememberSelection] = useState(true);

  const isSuperAdmin = userData.user.issuperadmin;

  useEffect(() => {
    const lastSelection = localStorage.getItem('ltms_last_selection');
    if (rememberSelection && lastSelection) {
      try {
        const { companyId } = JSON.parse(lastSelection);
        const hasAccess = userData.companies.some(c => c.companyid === companyId);
        if (hasAccess) { setSelectedCompany(companyId.toString()); return; }
      } catch {
        localStorage.removeItem('ltms_last_selection');
      }
    }
    if (userData.companies?.length > 0) {
      const defaultCompany = userData.companies.find(c => c.isdefault) || userData.companies[0];
      setSelectedCompany(defaultCompany.companyid.toString());
    }
  }, [userData, rememberSelection]);

  useEffect(() => {
    if (selectedCompany) fetchFinancialYears(selectedCompany);
    else { setFinancialYears([]); setSelectedYear(''); }
  }, [selectedCompany]);

  const fetchFinancialYears = async (companyId) => {
    setYearsLoading(true); setError(''); setSelectedYear('');
    try {
      const response = await getFinancialYears(companyId);
      if (response.success) {
        setFinancialYears(response.data);
        const lastSelection = localStorage.getItem('ltms_last_selection');
        let yearSet = false;
        if (rememberSelection && lastSelection) {
          try {
            const { companyId: lastCompanyId, yearId } = JSON.parse(lastSelection);
            if (lastCompanyId === parseInt(companyId)) {
              const yearExists = response.data.some(y => y.yearid === yearId);
              if (yearExists) { setSelectedYear(yearId.toString()); yearSet = true; }
            }
          } catch { /* ignore */ }
        }
        if (!yearSet) {
          const currentYear = response.data.find(y => y.iscurrent);
          if (currentYear) setSelectedYear(currentYear.yearid.toString());
          else if (response.data.length > 0) setSelectedYear(response.data[0].yearid.toString());
        }
      } else {
        setError(response.message || 'Failed to fetch financial years');
        setFinancialYears([]);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching financial years');
      setFinancialYears([]);
    } finally {
      setYearsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (!selectedCompany || !selectedYear) { setError('Please select both company and financial year'); return; }
    setLoading(true);
    try {
      const response = await createSession(userData.user.userid, parseInt(selectedCompany), parseInt(selectedYear));
      if (response.success) {
        if (rememberSelection) {
          localStorage.setItem('ltms_last_selection', JSON.stringify({ companyId: parseInt(selectedCompany), yearId: parseInt(selectedYear) }));
        } else {
          localStorage.removeItem('ltms_last_selection');
        }
        const sessionData = {
          user: { userid: response.data.session.userid, username: response.data.session.username, fullname: response.data.session.fullname, issuperadmin: response.data.session.issuperadmin },
          company: { companyid: response.data.session.companyid, companyname: response.data.session.companyname, companycode: response.data.session.companycode },
          financial_year: { yearid: response.data.session.yearid, yearname: response.data.session.yearname, yearcode: response.data.session.yearcode, startdate: response.data.session.startdate, enddate: response.data.session.enddate, iscurrent: response.data.session.iscurrent },
          session: response.data.session
        };
        console.log('📦 Transformed session data:', sessionData);
        onSelectionComplete(sessionData);
      } else {
        setError(response.message || 'Failed to create session');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while creating session');
    } finally {
      setLoading(false);
    }
  };
  const handleLogout = async () => {
      try {
        await logoutUser();    // ✓ actually calls logout.php
      } catch (error) {
        console.error('Logout error:', error);
      }
      onLogout();               
  };
  const handleCompanyChange = (e) => { setSelectedCompany(e.target.value); setSelectedYear(''); };

  const company = userData.companies.find(c => c.companyid === parseInt(selectedCompany));
  const year = financialYears.find(y => y.yearid === parseInt(selectedYear));

  // shared select style
  const selectCls = (hasValue) =>
    `w-full px-4 py-3 border-2 rounded-lg text-[15px] text-gray-700 bg-white cursor-pointer
    appearance-none outline-none transition-all duration-200
    focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)]
    disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60
    ${hasValue ? 'border-emerald-400 bg-emerald-50' : 'border-[#CBD5E1] hover:border-[#2563EB] focus:border-[#2563EB]'}`;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-5"
      style={{ background: '#F8FAFC' }}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[600px] overflow-hidden animate-[slideUp_0.4s_ease-out] border border-slate-200"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}
      >
        {/* ── Header ── */}
        <div
          className="text-white px-8 py-10 text-center"
          style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)' }}
        >
           
          <h2 className="text-[28px] font-bold m-0 mb-2">Select Company & Financial Year</h2>
          <p className="text-lg opacity-95 m-0 mb-1">
            Welcome back, <strong>{userData.user.fullname}</strong>!
            {isSuperAdmin && <span className="text-cyan-300 font-semibold"> (Super Admin)</span>}
          </p>
          <p className="text-sm opacity-85 m-0">
            {isSuperAdmin
              ? 'You have full system access. Select any company and financial year to continue.'
              : 'Please select your company and financial year to continue'}
          </p>
        </div>

        {/* ── Form ── */}
        <form onSubmit={handleSubmit} className="px-8 py-8">

          {/* Company select */}
          <div className="mb-6">
            <label htmlFor="company" className="block mb-2 font-semibold text-gray-700 text-sm">
              Company <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="relative">
              <select
                id="company"
                value={selectedCompany}
                onChange={handleCompanyChange}
                disabled={loading || yearsLoading}
                required
                className={selectCls(!!selectedCompany)}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', paddingRight: '40px' }}
              >
                <option value="">-- Select Company --</option>
                {userData.companies.map(c => (
                  <option key={c.companyid} value={c.companyid}>
                    {c.companyname} ({c.companycode}){c.isdefault ? ' ⭐' : ''}
                  </option>
                ))}
              </select>
            </div>
            {userData.companies.length > 1 && (
              <small className="block mt-1.5 text-[13px] text-gray-500 italic">
                {isSuperAdmin ? `Full access to all ${userData.companies.length} companies` : `You have access to ${userData.companies.length} companies`}
              </small>
            )}
          </div>

          {/* Company info card */}
          {company && (
            <div className="mb-6 border-2 border-gray-200 rounded-lg overflow-hidden animate-[fadeInDown_0.3s_ease-out]">
              <div className="px-4 py-3 text-white text-sm font-semibold" 
                style={{ 
                        background: 'linear-gradient(135deg,#2563EB 0%,#1D4ED8 100%)',
                        color: '#ffffff'   
                      }}>
                Selected Company
              </div>
              <div className="px-4 py-4 bg-gray-50">
                <p className="font-semibold text-gray-800 mb-1">{company.companyname}</p>
                <p className="text-[13px] text-gray-500 mb-2">Code: {company.companycode}</p>
                {company.isdefault && (
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#2563EB' }}>
                    Default Company
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Year select */}
          <div className="mb-6">
            <label htmlFor="year" className="block mb-2 font-semibold text-gray-700 text-sm">
              Financial Year <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="relative">
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                disabled={loading || yearsLoading || !selectedCompany}
                required
                className={selectCls(!!selectedYear)}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', paddingRight: '40px' }}
              >
                <option value="">{yearsLoading ? 'Loading...' : '-- Select Financial Year --'}</option>
                {financialYears.map(y => (
                  <option key={y.yearid} value={y.yearid}>
                    {y.yearname} ({y.yearcode}){y.iscurrent ? ' 🟢 Current' : ''}
                  </option>
                ))}
              </select>
            </div>
            {yearsLoading && (
              <div className="flex items-center gap-2.5 mt-2 text-[#2563EB] text-sm">
                <span className="w-4 h-4 border-2 border-gray-200 border-t-[#2563EB] rounded-full animate-spin inline-block" />
                Loading financial years...
              </div>
            )}
            {!yearsLoading && financialYears.length > 0 && (
              <small className="block mt-1.5 text-[13px] text-gray-500 italic">
                {financialYears.length} financial year{financialYears.length > 1 ? 's' : ''} available
              </small>
            )}
          </div>

          {/* Year info card */}
          {year && (
            <div className="mb-6 border-2 border-gray-200 rounded-lg overflow-hidden animate-[fadeInDown_0.3s_ease-out]">
              <div className="px-4 py-3 text-white text-sm font-semibold"
                style={{ 
                        background: 'linear-gradient(135deg,#2563EB 0%,#1D4ED8 100%)',
                        color: '#ffffff'   
                      }}>
                Selected Financial Year
              </div>
              <div className="px-4 py-4 bg-gray-50">
                <p className="font-semibold text-gray-800 mb-1">{year.yearname}</p>
                <p className="text-[13px] text-gray-500 mb-1">Code: {year.yearcode}</p>
                <p className="text-[13px] text-gray-500 mb-2">
                  Period: {new Date(year.startdate).toLocaleDateString()} to {new Date(year.enddate).toLocaleDateString()}
                </p>
                {year.iscurrent && (
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#16A34A' }}>
                    Current Year
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Remember checkbox */}
          <div className="mb-6">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberSelection}
                onChange={(e) => setRememberSelection(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 cursor-pointer accent-[#2563EB]"
              />
              <span className="text-sm text-gray-600">Remember my selection for next time</span>
            </label>
          </div>

          {/* Error */}
          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-5">
              <strong className="block mb-1">⚠️ Error:</strong> {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              disabled={loading || yearsLoading || !selectedCompany || !selectedYear}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 text-[15px] font-semibold
                text-white border-0 rounded-lg cursor-pointer transition-all duration-200
                hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(37,99,235,0.4)]
                active:translate-y-0
                disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              style={{ background: 'linear-gradient(135deg,#2563EB 0%,#1D4ED8 100%)', color: '#fff' }}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Creating session...</span>
                </>
              ) : (
                <>
                  <span>Continue to Dashboard</span>
                  <span className="text-lg font-bold">→</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleLogout}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 text-[15px] font-semibold
                text-gray-600 bg-white border-2 border-[#CBD5E1] rounded-lg cursor-pointer
                transition-all duration-200 hover:bg-gray-50 hover:border-gray-400
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="text-lg font-bold">←</span>
              <span>Logout</span>
            </button>
          </div>
        </form> 
      </div>
    </div>
  );
};

export default CompanyYearSelection;